/**
 * useInspections — read query against public.inspections.
 *
 * Scope is enforced by RLS:
 *   - super_admin              sees everything
 *   - supervisor / contractor_admin sees own org
 *   - client_view              sees own org
 *   - cleaner                  sees inspections on their OWN task
 *
 * `client_view` does not get internal notes — the row is fetched
 * but the `notes` field is dropped at the hook layer for that role,
 * to match the spec's "client_view cannot see internal notes."
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { useSupabase }                    from './useSupabase'
import { useRole }                        from './useAuth'

export type InspectionStatus =
  | 'pending'
  | 'in_progress'
  | 'passed'
  | 'failed'
  | 're_clean_required'
  | 'blocked_by_maintenance'
  | 'blocked_by_owner_approval'
  | 'cancelled'

export interface InspectionRow {
  id:                   string
  organization_id:      string
  room_id:              string
  cleaning_task_id:     string
  inspector_user_id:    string
  status:               InspectionStatus
  pass_number:          number
  failure_reason:       string | null
  photo_storage_paths:  string[]
  re_clean_required:    boolean
  notes:                string | null
  started_at:           string | null
  inspected_at:         string | null
  created_at:           string
  updated_at:           string
}

export interface UseInspectionsArgs {
  /** When set, filters to a single task — multi-pass list ordered by pass_number ASC. */
  cleaningTaskId?: string
  /** When set, filters to one or more statuses. */
  statuses?:       InspectionStatus[]
}

export function useInspections(
  args: UseInspectionsArgs = {},
): UseQueryResult<InspectionRow[]> {
  const supabase  = useSupabase()
  const role      = useRole()
  const cleaningTaskId = args.cleaningTaskId
  const statuses       = args.statuses

  return useQuery({
    queryKey: ['inspections', { cleaningTaskId: cleaningTaskId ?? null, statuses: statuses ?? null }],
    refetchInterval: cleaningTaskId ? false : 30_000,
    queryFn: async (): Promise<InspectionRow[]> => {
      let query = supabase
        .from('inspections')
        .select(
          'id, organization_id, room_id, cleaning_task_id, inspector_user_id,' +
          ' status, pass_number, failure_reason, photo_storage_paths,' +
          ' re_clean_required, notes, started_at, inspected_at,' +
          ' created_at, updated_at',
        )

      if (cleaningTaskId) {
        // Multi-pass list — pass_number ASC for readable history order.
        query = query.eq('cleaning_task_id', cleaningTaskId).order('pass_number', { ascending: true })
      } else {
        query = query.order('created_at', { ascending: false })
      }

      if (statuses && statuses.length > 0) {
        query = query.in('status', statuses as unknown as string[])
      }

      const { data, error } = await query
      if (error) throw error
      const rows = (data ?? []) as unknown as InspectionRow[]

      // client_view cannot see internal notes — drop the field after
      // fetch so the UI cannot accidentally surface it. Server-side
      // column-level RLS would be the canonical guard; until that
      // ships, the hook is the bottleneck for client_view reads.
      if (role === 'client_view') {
        return rows.map((r) => ({ ...r, notes: null }))
      }

      return rows
    },
  })
}
